/**
 * Tooltip - port from `1116/omega-club/dashboard/src/components/Tooltip.jsx`.
 * Hover/click tooltip with smart positioning (flip if it would overflow
 * the viewport).
 *
 * Mouse: hover to show, pointer-leave to hide.
 * Touch: tap to toggle; tap outside to dismiss.
 *
 * Per CHAP-89 (Phase 1C #55).
 */
import { useState, useRef, useEffect, useCallback, type ReactNode, type PointerEvent } from 'react';

interface Props {
  children: ReactNode;
  /** Tooltip body content. */
  content: ReactNode;
  /** Optional citation source rendered as italic at the bottom. */
  source?: string;
}

export function Tooltip({ children, content, source }: Props) {
  const [visible, setVisible] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [position, setPosition] = useState({ top: true, left: false });
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      setPosition({
        top: triggerRect.top > tooltipRect.height + 8,
        left: triggerRect.left + tooltipRect.width > viewportWidth - 16,
      });
      requestAnimationFrame(() => setPositioned(true));
    } else {
      setPositioned(false);
    }
  }, [visible]);

  const handleClickOutside = useCallback((e: PointerEvent | Event) => {
    if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      document.addEventListener('pointerdown', handleClickOutside as unknown as EventListener);
      return () => document.removeEventListener('pointerdown', handleClickOutside as unknown as EventListener);
    }
    return undefined;
  }, [visible, handleClickOutside]);

  const handlePointerEnter = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse') setVisible(true);
  };
  const handlePointerLeave = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse') setVisible(false);
  };
  const handleTap = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') {
      e.preventDefault();
      setVisible((v) => !v);
    }
  };

  return (
    <div
      ref={triggerRef}
      className="relative inline-block"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerUp={handleTap}
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 max-w-[280px] sm:max-w-[300px] p-2.5 sm:p-3 rounded-lg bg-gray-900/95 text-white text-xs leading-relaxed shadow-xl pointer-events-none transition-opacity duration-150
            ${positioned ? 'opacity-100' : 'opacity-0'}
            ${position.top ? 'bottom-full mb-2' : 'top-full mt-2'}
            ${position.left ? 'right-0' : 'left-0'}
          `}
        >
          <div>{content}</div>
          {source && (
            <p className="mt-1.5 text-gray-400 text-xs italic">Source: {source}</p>
          )}
        </div>
      )}
    </div>
  );
}
